import * as React from "react";
import {
    Card,
    CardContent,
    FormControl,
    Grid,
    InputAdornment,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    TextField,
    Typography,
    WithStyles
} from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import { styles } from "./OrderDetailsView.styles";
import { adminFeeDefault, adminFeeLimit, deliveryMethod, orderStats, orderTypes } from "../../constants/ORDER_DETAILS";
import { AddressCard } from "../components/OrderDetails/AddressCard";
import { convertDateToFullDateTime } from "../../utilities/dateTimeUtils";
import { ccyFormat } from "../../utilities/PriceUtils";
import { useTranslation } from "react-i18next";
import { isPriceValid } from "../../utilities/generalUtils";
import PermissionGuard from "../components/PermissionGuard/PermissionGuard";
import { ProductsTable } from "../components/OrderDetails/ProductsTable";
import { BlueButton } from "../components/Buttons/BlueButton";
import { useSelector } from "react-redux";
import { selectVisibleCountries } from "../../selectors/countries/CountriesSelector";
import { isEuCountry } from "../../utilities/countriesUtils";

export interface IOrderDetailsComponentProps extends WithStyles<typeof styles> {
    order: any,
    orderId: string,
    userId: string,
    updateOrder: any,
    showAdminOtherUser: boolean,
    detailsChanged: boolean,
    setDetailsChanged: any
}

const OrderDetailsComponentPure: React.FunctionComponent<IOrderDetailsComponentProps> = ({
                                                                                             classes,
                                                                                             order,
                                                                                             orderId,
                                                                                             userId,
                                                                                             updateOrder,
                                                                                             showAdminOtherUser,
                                                                                             detailsChanged,
                                                                                             setDetailsChanged
                                                                                         }: IOrderDetailsComponentProps) => {

    const {t} = useTranslation();
    const countries = useSelector(state => selectVisibleCountries(state));

    const [orderDetails, setOrderDetails] = React.useState({
        orderId: orderId,
        userId: userId,
        orderState: order["state"],
        type: order["type"],
        adminFee: order["adminFee"],
        discount: order["discount"],
        totalPrice: order["totalPrice"],
        vat: order["vat"],
        remark: order["remark"] || ""
    });

    React.useEffect(() => {
        setOrderDetails({
            ...order,
            orderState: order["state"]
        });
    }, [order, setOrderDetails]);

    const handleChange = (event) => {
        const name = event.target.name;
        const value = event.target.value;
        setOrderDetails({
            ...orderDetails,
            [name]: value
        });
        setDetailsChanged(true);
    };

    const localUpdateOrder = (event) => {
        event.preventDefault();
        order.state = orderDetails.orderState;
        order.type = orderDetails.type;
        order.adminFee = adminFee;
        order.discount = orderDetails.discount;
        order.totalPrice = orderDetails.totalPrice > 0 ? orderDetails.totalPrice : 0;
        order.vat = orderDetails.vat;
        order.remark = orderDetails.remark;

        updateOrder(order);

        setDetailsChanged(false);
    };

    let subtotalPrice = 0.0;
    for (let ord of order.items) {
        subtotalPrice += ord.price;
    }

    let adminFee = orderDetails.adminFee;
    if (detailsChanged && ((order.items.length !== 0) && (Number(orderDetails.discount) !== subtotalPrice && subtotalPrice <= adminFeeLimit))) {
        adminFee = adminFeeDefault;
    } //set the admin fee in the frontend only if this is not a custom data order

    let subtotalPriceNoVat = subtotalPrice + Number(adminFee) - Number(orderDetails.discount);
    let vatValue = (subtotalPriceNoVat * Number(orderDetails.vat)) / 100;
    let totalPriceFrontend = subtotalPriceNoVat + vatValue;

    return <div>

        <Grid className={classes.bottomMargin} container direction="row" alignItems="flex-start">
            <Grid item xs={6}>
                <Card className={classes.card}>
                    <CardContent>
                        <Typography variant="body2">{t("orderDetails.userEmail")} {order["email"]}</Typography>
                        <Typography variant="body2">{t("orderDetails.orderId")}: {orderId}</Typography>
                        <Typography variant="body2">{t("general.status")}: {orderDetails.orderState}</Typography>
                        <Typography
                            variant="body2">{t("general.date")}: {convertDateToFullDateTime(order["created"])}</Typography>
                        <Typography variant="body2">{t("orderDetails.paymentMethod")} {order["paymentMethod"]}</Typography>
                        {showAdminOtherUser && order["paymentMethod"] === "Card"
                            ? <Typography variant="body2">{t("orderDetails.paymentId")} {order["paymentId"]}</Typography>
                            : null
                        }
                        {order["tin"] !== ""
                            ? <Typography variant="body2">VAT ID: {order["tin"]}</Typography>
                            : null
                        }
                    </CardContent>
                </Card>
            </Grid>
            <Grid item xs={3}>
                <AddressCard float address={order["billingAddress"]} title={t("orderDetails.title.billingAddress")} />
            </Grid>
            <Grid item xs={3}>
                <AddressCard float address={order["deliveryAddress"]} title={t("general.deliveryAddress")} />
            </Grid>
        </Grid>

        {showAdminOtherUser
            ? <div>
                <Grid container spacing={2} direction="row" alignItems="center">
                    <Grid item xs={2}>
                        <Typography variant="h2" className={classes.manageOrderTitle}>{t("orderDetails.manageOrder")}</Typography>
                    </Grid>
                    <Grid item xs={2}>
                        <PermissionGuard scope={["orders.write", "orders.status.write"]}>
                            <BlueButton
                                disabled={!detailsChanged}
                                style={{marginTop: 10}}
                                onClick={localUpdateOrder}
                            >{t("orderDetails.saveChanges")}
                            </BlueButton>
                        </PermissionGuard>
                    </Grid>
                </Grid>

                <Grid className={classes.bottomMargin} container spacing={3} direction="row" alignItems="center">
                    <Grid item xs={12} sm={3}>
                        <Typography variant="h3" className={classes.sectionTitle}>{t("orderDetails.orderStatus")}</Typography>
                        <PermissionGuard scope="orders.status.write" disabled tooltip="You don't have permission">
                            <FormControl required variant="outlined" className={classes.formControl}>
                                <Select
                                    labelId="orderState"
                                    id="orderState"
                                    name="orderState"
                                    value={orderDetails.orderState}
                                    onChange={handleChange}
                                >
                                    {orderStats.map(os => (
                                        <MenuItem key={os} value={os}>{os}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </PermissionGuard>
                    </Grid>

                    <Grid item xs={12} sm={3}>
                        <Typography variant="h3" className={classes.sectionTitle}>{t("orderDetails.orderType")}</Typography>
                        <PermissionGuard scope="orders.write" disabled tooltip="You don't have permission">
                            <FormControl required variant="outlined" className={classes.formControl}>
                                <Select
                                    labelId="orderType"
                                    id="ordertype"
                                    name="type"
                                    value={orderDetails.type}
                                    onChange={handleChange}
                                >
                                    {orderTypes.map(ot => (
                                        <MenuItem key={ot} value={ot}>{ot}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </PermissionGuard>
                    </Grid>
                </Grid>
            </div>
            : null
        }

        <div className={classes.details}>
            <Typography variant="h3" className={classes.sectionTitle}>{t("orderDetails.title.orderDetails")}</Typography>
            <ProductsTable tableItems={order["items"]} />

            <Grid className={classes.bottomMargin} container spacing={3} direction="row">
                {showAdminOtherUser
                    ? <Grid item xs={12} sm={6}>
                        <Typography variant="h3" className={classes.sectionTitle}>{t("orderDetails.orderComments")}</Typography>
                        <Typography variant="body1">{t("orderDetails.updateOrderComments")}</Typography>
                        <PermissionGuard scope="orders.write" disabled tooltip="You don't have permission">
                            <TextField
                                className={classes.comment}
                                variant="outlined"
                                name="remark"
                                type="text"
                                id="remark"
                                multiline
                                rows={8}
                                value={orderDetails.remark}
                                onChange={handleChange}
                            />
                        </PermissionGuard>
                    </Grid>
                    : <Grid item xs={12} sm={6}>
                        <Typography variant="h4">{t("orderDetails.orderComments")}</Typography>
                        <Typography
                            variant="body1">{orderDetails.remark ? orderDetails.remark : t("orderDetails.noComment")}</Typography>
                    </Grid>
                }

                <Grid item xs={12} sm={6}>
                    <TableContainer>
                        <Table>
                            <TableBody>
                                <TableRow>
                                    <TableCell colSpan={4} align="left">{t("orderDetails.subtotal")}</TableCell>
                                    <TableCell align="right">{ccyFormat(Number(subtotalPrice))}</TableCell>
                                </TableRow>

                                <TableRow>
                                    <TableCell colSpan={4} align="left">{t("orderDetails.adminFee")}</TableCell>
                                    <TableCell align="right">{ccyFormat(Number(adminFee))}</TableCell>
                                </TableRow>

                                {showAdminOtherUser
                                    ? <TableRow>
                                        <TableCell colSpan={4} align="left">{t("orderDetails.discount")}</TableCell>
                                        <TableCell align="right">
                                            <PermissionGuard scope="orders.write" disabled tooltip="You don't have permission">
                                                <TextField
                                                    className={classes.updateFieldSmall}
                                                    variant="outlined"
                                                    size="small"
                                                    name="discount"
                                                    id="discount"
                                                    type="text"
                                                    InputProps={{
                                                        startAdornment: <InputAdornment position="start">€</InputAdornment>
                                                    }}
                                                    value={orderDetails.discount}
                                                    onChange={(event) => {
                                                        if ((isPriceValid(event.target.value) && Number(event.target.value) <= (Number(subtotalPrice) + Number(adminFee))) || event.target.value === "") {
                                                            setOrderDetails({
                                                                ...orderDetails,
                                                                [event.target.name]: event.target.value
                                                            });
                                                            setDetailsChanged(true);

                                                            var currentDiscount = parseFloat(event.target.value).toFixed(2);
                                                            if (Number(parseFloat(subtotalPrice.toString()).toFixed(2)) - Number(currentDiscount) === 0) {
                                                                setOrderDetails({
                                                                    ...orderDetails,
                                                                    discount: event.target.value,
                                                                    adminFee: 0,
                                                                    totalPrice: 0
                                                                });
                                                            }
                                                        }
                                                    }}
                                                />
                                            </PermissionGuard>
                                        </TableCell>
                                    </TableRow>
                                    : <TableRow>
                                        <TableCell colSpan={4} align="left">{t("orderDetails.discount")}</TableCell>
                                        <TableCell align="right">{ccyFormat(Number(orderDetails.discount).toFixed(2))}</TableCell>
                                    </TableRow>
                                }

                                <TableRow>
                                    <TableCell colSpan={4} align="left">{t("orderDetails.deliveryMethod")}</TableCell>
                                    <TableCell
                                        align="right">{order["delivery_method"] ? order["delivery_method"] : deliveryMethod}
                                    </TableCell>
                                </TableRow>

                                {isEuCountry(countries, order["deliveryAddress"].country)
                                    ? <>
                                        <TableRow>
                                            <TableCell colSpan={4} align="left">
                                                {t("general.totalAmountNoVat")}
                                            </TableCell>
                                            <TableCell align="right">
                                                {ccyFormat(subtotalPriceNoVat)}
                                            </TableCell>
                                        </TableRow>

                                        <TableRow>
                                            <TableCell colSpan={4} align="left">
                                                {t("orderDetails.vat")} ({orderDetails.vat}%)
                                            </TableCell>
                                            <TableCell align="right">
                                                {ccyFormat(vatValue)}
                                            </TableCell>
                                        </TableRow>

                                        <TableRow>
                                            <TableCell className={classes.bold} colSpan={4} align="left">
                                                {t("general.totalAmountVat")}
                                            </TableCell>
                                            <TableCell className={classes.bold} align="right">
                                                {ccyFormat(totalPriceFrontend)}
                                            </TableCell>
                                        </TableRow>
                                    </>
                                    : <>
                                        <TableRow>
                                            <TableCell className={classes.bold} colSpan={4} align="left">
                                                Total amount (net amount)
                                            </TableCell>
                                            <TableCell className={classes.bold} align="right">
                                                {ccyFormat(totalPriceFrontend)}
                                            </TableCell>
                                        </TableRow>
                                    </>
                                }                               

                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            </Grid>
        </div>
    </div>;
};

export const OrderDetailsComponent = withStyles(styles)(OrderDetailsComponentPure);
